"use client";

import React, { useCallback, useRef, useLayoutEffect, useMemo } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useBounceLink, UseBounceLinkOptions } from "./use-bounce-link";
import "./animated-link.css";

type ElementType = keyof React.JSX.IntrinsicElements;

/**
 * AnimatedLink - A component with a smooth bounce animation on hover.
 *
 * Can be used as a link or any other element (button, div, etc.).
 * Preserves all existing styling while adding a text bounce effect.
 * The hover text color inherits from the element's current color.
 *
 * @example
 * ```tsx
 * // Basic usage with Next.js Link
 * <AnimatedLink href="/about">About</AnimatedLink>
 *
 * // With custom styling
 * <AnimatedLink
 *   href="/shop"
 *   className="text-link-primary font-semibold"
 *   hoverCircleColor="#0b3b66"
 * >
 *   Shop Now
 * </AnimatedLink>
 *
 * // External link
 * <AnimatedLink href="https://example.com" target="_blank">
 *   External Site
 * </AnimatedLink>
 *
 * // Without hover circle (text animation only)
 * <AnimatedLink href="/about" showHoverCircle={false}>
 *   About
 * </AnimatedLink>
 *
 * // As a button (no href)
 * <AnimatedLink as="button" onClick={() => console.log('clicked')}>
 *   Click Me
 * </AnimatedLink>
 *
 * // As a div
 * <AnimatedLink as="div" className="cursor-pointer">
 *   Animated Div
 * </AnimatedLink>
 * ```
 */
export interface AnimatedLinkProps extends UseBounceLinkOptions {
  href?: string;
  as?: ElementType;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  target?: string;
  rel?: string;
  ariaLabel?: string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}

const isExternalLink = (href: string): boolean =>
  href.startsWith("http://") ||
  href.startsWith("https://") ||
  href.startsWith("//") ||
  href.startsWith("mailto:") ||
  href.startsWith("tel:") ||
  href.startsWith("#");

const isRouterLink = (href?: string): boolean => (href ? !isExternalLink(href) : false);

export const AnimatedLink = React.forwardRef<HTMLElement, AnimatedLinkProps>(
  (
    {
      href,
      as,
      children,
      className,
      style,
      hoverCircleColor,
      ease,
      duration,
      showHoverCircle,
      onClick,
      target,
      rel,
      ariaLabel,
      type,
      disabled,
    },
    ref
  ) => {
    const { linkRef, circleRef, labelRef, hoverLabelRef, handleEnter, handleLeave } = useBounceLink({
      hoverCircleColor,
      ease,
      duration,
      showHoverCircle,
    });

    // Local ref to merge with forwarded ref
    const localRef = useRef<HTMLElement | null>(null);

    // Sync localRef to linkRef from hook (needed for animation)
    useLayoutEffect(() => {
      if (linkRef && "current" in linkRef) {
        linkRef.current = localRef.current;
      }
    });

    // Merge forwarded ref with local ref
    const mergedRef = useCallback(
      (el: HTMLElement | null) => {
        localRef.current = el;
        if (typeof ref === "function") {
          ref(el);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = el;
        }
      },
      [ref]
    );

    const linkContent = (
      <>
        {showHoverCircle !== false && <span className='hover-circle' aria-hidden='true' ref={circleRef} />}
        <span className='label-stack'>
          <span className='link-label' ref={labelRef}>
            {children}
          </span>
          <span className='link-label-hover' aria-hidden='true' ref={hoverLabelRef}>
            {children}
          </span>
        </span>
      </>
    );

    // Memoize commonProps to avoid dependency issues
    const commonProps = useMemo(
      () => ({
        className: cn("animated-link", className),
        style,
        onMouseEnter: handleEnter,
        onMouseLeave: handleLeave,
        onClick,
        "aria-label": ariaLabel,
      }),
      [className, style, handleEnter, handleLeave, onClick, ariaLabel]
    );

    // Determine element type early (before conditionals)
    const elementType = as || "button";

    // Memoize element props for dynamic element types (before conditionals to satisfy rules of hooks)
    const elementProps = useMemo(
      () => ({
        ...commonProps,
        ref: mergedRef,
      }),
      [commonProps, mergedRef]
    );

    // If href is provided, render as link
    if (href) {
      if (isRouterLink(href)) {
        return (
          <Link href={href} ref={mergedRef as React.ForwardedRef<HTMLAnchorElement>} {...commonProps}>
            {linkContent}
          </Link>
        );
      }

      return (
        <a
          href={href}
          ref={mergedRef as React.ForwardedRef<HTMLAnchorElement>}
          target={target}
          rel={rel}
          {...commonProps}
        >
          {linkContent}
        </a>
      );
    }

    // If no href, render as specified element (defaults to button)
    if (elementType === "button") {
      return (
        <button
          ref={mergedRef as React.ForwardedRef<HTMLButtonElement>}
          type={type || "button"}
          disabled={disabled}
          {...commonProps}
        >
          {linkContent}
        </button>
      );
    }

    if (elementType === "div") {
      return (
        <div ref={mergedRef as React.ForwardedRef<HTMLDivElement>} {...commonProps}>
          {linkContent}
        </div>
      );
    }

    // For other element types, use React.createElement
    // React.ElementType is the proper type for dynamic element creation
    // eslint-disable-next-line react-hooks/refs -- elementProps contains a ref callback (not ref.current), which is safe to pass to React.createElement
    return React.createElement(elementType as React.ElementType, elementProps, linkContent);
  }
);

AnimatedLink.displayName = "AnimatedLink";

export default AnimatedLink;
